import { Fragment, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Divider,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Warning as WarningIcon } from '@mui/icons-material';
import { AgencyState } from './useAgencyData';

interface AgencyPanelProps {
  agency: AgencyState;
  reporterEmail: string | null;
  addEmail: string;
  setAddEmail: (v: string) => void;
  addLoading: boolean;
  addWarning: string | null;
  addError: string | null;
  onAdd: () => void;
  onRemove: (email: string) => void;
}

const AgencyEmailPanel = ({
  agency, reporterEmail, addEmail, setAddEmail, addLoading, addWarning, addError, onAdd, onRemove,
}: AgencyPanelProps) => {
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [domainWarning, setDomainWarning] = useState<string | null>(null);

  const agencyDomain = reporterEmail ? (reporterEmail.split('@')[1]?.toLowerCase() ?? null) : null;

  const handleEmailChange = (val: string) => {
    setAddEmail(val);
    if (!agencyDomain || !val.includes('@')) { setDomainWarning(null); return; }
    const typedDomain = val.split('@')[1]?.toLowerCase();
    if (typedDomain && typedDomain !== agencyDomain) {
      setDomainWarning(`Domain doesn't match the agency domain (@${agencyDomain})`);
    } else {
      setDomainWarning(null);
    }
  };

  return (
    <Box>
      {addWarning && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          {addWarning}
        </Alert>
      )}
      {agency.authorizedEmails.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No authorized reporters yet.
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          {agency.authorizedEmails.map((e, i) => (
            <Fragment key={e.id}>
              {i > 0 && <Divider />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{e.email}</Typography>
                  <Typography variant="caption" color="text.secondary">Added {new Date(e.createdAt).toLocaleDateString()} by {e.createdBy}</Typography>
                </Box>
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => setPendingDelete(e.email)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Fragment>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Add email"
          value={addEmail}
          onChange={e => handleEmailChange(e.target.value)}
          error={!!addError}
          disabled={addLoading}
          onKeyDown={e => e.key === 'Enter' && !addLoading && onAdd()}
          InputLabelProps={{ sx: { '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 12px) scale(1)' } } }}
          sx={{ flex: 1 }}
          style={{ marginBottom: 0 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd} disabled={addLoading || !addEmail} sx={{ borderRadius: 2, flexShrink: 0 }} style={{ height: 48 }}>
          {addLoading ? 'Adding…' : 'Add'}
        </Button>
      </Box>
      {addError && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{addError}</Typography>}
      {domainWarning && <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'warning.main' }}>{domainWarning}</Typography>}

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove {pendingDelete}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove their access. Their passkeys and any draft worksheets will also be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { onRemove(pendingDelete!); setPendingDelete(null); }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgencyEmailPanel;
